import React, { useCallback, useEffect, useRef, useState } from "react";

import {
  Alert,
  Box,
  Button,
  Chip,
  Collapse,
  LinearProgress,
  Paper,
  Stack,
  Typography,
} from "@mui/material";

import { sectionLabelSx } from "src/shell/styles/themeUtils";

import {
  listAppsByResource,
  type ResourceAppRecord,
} from "../../../../application/module/api/secretsApi";

// ── Types ─────────────────────────────────────────────────────────────────────

interface ApplicationWiringSectionProps {
  resourceAlias: string;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function getEnvVars(
  record: ResourceAppRecord,
): { name: string; redacted: boolean }[] {
  const prefix = record.alias.toUpperCase().replace(/-/g, "_");
  switch (record.resource.resourceType) {
    case "postgres":
      return [
        { name: `${prefix}_DATABASE_URL`, redacted: true },
        { name: `${prefix}_DIRECT_URL`, redacted: true },
        { name: `${prefix}_DATABASE_USERNAME`, redacted: true },
        { name: `${prefix}_DATABASE_PASSWORD`, redacted: true },
        { name: `${prefix}_DATABASE_NAME`, redacted: true },
      ];
    case "nats":
      return [
        { name: `${prefix}_NATS_URL`, redacted: false },
        { name: `${prefix}_NATS_CREDS`, redacted: true },
      ];
    case "redis":
      return [{ name: `${prefix}_REDIS_URL`, redacted: false }];
    default:
      return [];
  }
}

// ── Component ─────────────────────────────────────────────────────────────────

export function ApplicationWiringSection({
  resourceAlias,
}: ApplicationWiringSectionProps): React.ReactElement | null {
  const [apps, setApps] = useState<ResourceAppRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  // Guards against setState after unmount: the async fetch can resolve
  // after the effect cleanup has run (e.g. during test env teardown),
  // and a late dispatch would crash on the torn-down DOM/window.
  const aliveRef = useRef(true);
  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
    };
  }, []);

  const fetchApps = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listAppsByResource(resourceAlias);
      if (!aliveRef.current) return;
      setApps(data);
    } catch (err) {
      if (!aliveRef.current) return;
      setError(
        err instanceof Error
          ? err.message
          : "Failed to load application wiring",
      );
    } finally {
      if (aliveRef.current) {
        setLoading(false);
      }
    }
  }, [resourceAlias]);

  useEffect(() => {
    void fetchApps();
  }, [fetchApps]);

  const toggleExpand = (alias: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(alias)) next.delete(alias);
      else next.add(alias);
      return next;
    });
  };

  // Don't render anything if no apps are using this resource
  if (!loading && !error && apps.length === 0) return null;

  return (
    <Paper variant="outlined" sx={{ p: 3, mt: 3 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="subtitle2" sx={sectionLabelSx}>
          Application Wiring
        </Typography>
        {apps.length > 0 && (
          <Chip label={apps.length} size="small" variant="outlined" />
        )}
      </Stack>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Applications that use this resource. Copy the connection string above
        and add it as a stack secret in the application&apos;s Secrets tab.
      </Typography>

      {loading ? (
        <LinearProgress />
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : (
        <Stack spacing={1.5}>
          {apps.map((ar) => {
            const envVars = getEnvVars(ar);
            return (
              <Paper key={ar.id} variant="outlined" sx={{ p: 2 }}>
                <Stack
                  direction="row"
                  spacing={2}
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Box sx={{ flex: 1 }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="body2" fontWeight={600}>
                        {ar.application.alias}
                      </Typography>
                      <Chip
                        label={`local alias: ${ar.alias}`}
                        size="small"
                        color="info"
                        variant="outlined"
                      />
                      <Chip
                        label={ar.application.status}
                        size="small"
                        color={
                          ar.application.status === "running"
                            ? "success"
                            : "default"
                        }
                        variant="outlined"
                      />
                    </Stack>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ mt: 0.5, display: "block" }}
                    >
                      {ar.application.name}
                    </Typography>
                  </Box>
                  <Button size="small" onClick={() => toggleExpand(ar.alias)}>
                    {expanded.has(ar.alias) ? "Hide" : "Show"} env vars
                  </Button>
                </Stack>
                <Collapse in={expanded.has(ar.alias)}>
                  <Box
                    sx={{
                      mt: 1.5,
                      p: 1.5,
                      bgcolor: "action.hover",
                      borderRadius: 1,
                    }}
                  >
                    {envVars.map((envVar) => (
                      <Stack
                        key={envVar.name}
                        direction="row"
                        spacing={2}
                        sx={{ py: 0.25 }}
                      >
                        <Typography
                          variant="body2"
                          fontFamily="monospace"
                          sx={{
                            color: "primary.main",
                            fontWeight: 600,
                            minWidth: 280,
                          }}
                        >
                          {envVar.name}
                        </Typography>
                        <Typography
                          variant="body2"
                          fontFamily="monospace"
                          color="text.secondary"
                        >
                          {envVar.redacted
                            ? "••••••••••••••••"
                            : "(auto-injected)"}
                        </Typography>
                      </Stack>
                    ))}
                  </Box>
                </Collapse>
              </Paper>
            );
          })}
        </Stack>
      )}
    </Paper>
  );
}

export default ApplicationWiringSection;
